import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Search, Users, Calendar, Utensils, Briefcase, MapPin, ArrowRight, ShoppingBag, Compass, Newspaper, LogIn, Lock } from "lucide-react";
import { DiscoverCard, useDiscoverItems } from "@/components/home/DiscoverMarketplace";
import { useShowMemberCounts } from "@/hooks/useCommunityConfig";
import { FeedCard } from "@/components/feed/FeedCard";

// Seis atalhos (2 filas de 3 no telemóvel) — toda a navegação principal vive aqui; todas as rotas já existem em App.js
const quick = [
  { to: "/comunidade", key: "community", icon: Users, testid: "home-quick-community" },
  { to: "/eventos", key: "events", icon: Calendar, testid: "home-quick-events" },
  { to: "/marketplace", key: "marketplace", icon: ShoppingBag, testid: "home-quick-marketplace" },
  { to: "/restaurantes", key: "restaurants", icon: Utensils, testid: "home-quick-restaurants" },
  { to: "/empregos", key: "jobs", icon: Briefcase, testid: "home-quick-jobs" },
];

export default function Home() {
  const { t, lang } = useLang();
  const showCounts = useShowMemberCounts();
  const { user } = useAuth();
  const nav = useNavigate();
  const [city, setCity] = useState("");
  const [featuredEvents, setFE] = useState([]);
  const discover = useDiscoverItems(8);
  const [communities, setCM] = useState([]);
  const [cities, setCities] = useState([]);
  const [activity, setActivity] = useState(null);
  const [women, setWomen] = useState(null);
  const loadWomen = () => api.get("/communities/by-slug/angolanas-em-uk").then(r => setWomen(r.data)).catch(() => setWomen(null));

  useEffect(() => {
    Promise.allSettled([
      api.get("/events", { params: { featured: true } }).then(r => setFE(r.data.slice(0, 5))),
      api.get("/communities", { params: { kind: "community" } }).then(r => setCM(r.data.filter(g => g.slug !== "angolanas-em-uk").slice(0, 6))),
      api.get("/cities").then(r => setCities(r.data)),
      loadWomen(),
    ]);
  }, []);
  useEffect(() => {
    if (!user) { setActivity(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/feed", { params: { tab: "recent", limit: 3 } });
        const ids = (type) => data.items.filter((i) => i.type === type).map((i) => i.id).join(",");
        const mine = async (url, post_ids) => (post_ids ? api.get(url, { params: { post_ids } }).then((r) => r.data || {}).catch(() => ({})) : {});
        const [mc, mp] = await Promise.all([mine("/community-reactions/mine", ids("community")), mine("/profile-reactions/mine", ids("profile"))]);
        setActivity(data.items.map((i) => ({ ...i, mine: (i.type === "community" ? mc : mp)[i.id] ?? null })));
      } catch { setActivity([]); }
    })();
  }, [user]);
  const removeActivity = (x) => setActivity((prev) => (prev || []).filter((i) => !(i.type === x.type && i.id === x.id)));

  const submitSearch = (e) => {
    e.preventDefault();
    nav(`/comunidade${city ? `?city=${encodeURIComponent(city)}` : ""}`);
  };

  return (
    <div className="page fade-up" data-testid="home-page">
      {/* Hero — compacto no telemóvel, mantém identidade (faixa, grain, badge amarelo) */}
      <section className="relative -mx-4 px-4 pt-4 pb-5 sm:pt-8 sm:pb-8 mb-4 overflow-hidden" aria-labelledby="home-hero-title">
        <div className="absolute inset-0 auk-ribbon opacity-95" />
        <div className="auk-grain absolute inset-0" />
        <div className="relative z-10 max-w-2xl mx-auto text-white">
          <span className="inline-block text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 bg-[#ffd100] text-black rounded-full mb-2">{t("brand")}</span>
          <h1 id="home-hero-title" className="font-display font-extrabold text-2xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight mb-1.5 drop-shadow-sm">{t("hero_title")}</h1>
          <p className="text-white/90 text-sm mb-4 leading-snug">{t("hero_sub")}</p>

          <form onSubmit={submitSearch} role="search" aria-label={t("search_label")} className="bg-white rounded-2xl p-1.5 pl-3 shadow-xl">
            <label htmlFor="home-city-input" className="block text-[11px] font-semibold text-zinc-600 pt-1">{t("search_label")}</label>
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-[#c8102e] shrink-0" aria-hidden="true" />
              <input id="home-city-input" data-testid="home-city-input" value={city} onChange={(e) => setCity(e.target.value)} list="cities-list"
                     placeholder={t("city_placeholder")} autoComplete="off" enterKeyHint="search"
                     className="flex-1 min-w-0 bg-transparent text-black text-base outline-none placeholder:text-zinc-400 h-11" />
              <datalist id="cities-list">{cities.map(c => <option key={c.name} value={c.name} />)}</datalist>
              <button data-testid="home-search-btn" type="submit" aria-label={t("search")}
                      className="h-11 px-4 rounded-xl bg-black text-white text-sm font-semibold hover:bg-[#c8102e] focus-visible:ring-2 focus-visible:ring-[#ffd100] transition-colors flex items-center gap-1 shrink-0">
                <Search size={15} aria-hidden="true" /> <span>{t("search")}</span>
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Atalhos — 6 destinos, 2 filas de 3 no telemóvel */}
      <nav aria-label={t("quick_links")} className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3" data-testid="home-quick-links">
        {quick.map(({ to, key, icon: Icon, testid }) => (
          <Link key={key} to={to} data-testid={testid} className="flex flex-col items-center gap-1.5 py-2.5 rounded-2xl bg-white border border-zinc-200 text-zinc-800 hover:border-[#c8102e] focus-visible:ring-2 focus-visible:ring-[#c8102e] min-h-[64px] transition-colors">
            <span className="w-9 h-9 rounded-xl bg-[#c8102e]/10 text-[#c8102e] flex items-center justify-center" aria-hidden="true"><Icon size={18} /></span>
            <span className="text-[11px] font-semibold leading-none">{t(key)}</span>
          </Link>
        ))}
      </nav>

      {/* Explorar — linha discreta */}
      <Link to="/explorar" data-testid="home-explore-card" className="flex items-center gap-2 text-sm text-zinc-700 px-1 py-2 mb-4 min-h-[44px] hover:text-[#c8102e] focus-visible:ring-2 focus-visible:ring-[#c8102e] rounded-lg">
        <Compass size={16} className="text-[#c8102e] shrink-0" aria-hidden="true" />
        <span className="flex-1 min-w-0 leading-snug whitespace-nowrap overflow-hidden text-ellipsis"><span className="font-semibold">{t("home_explore_title")}</span> <span className="text-zinc-500 text-xs sm:text-sm">· {t("home_explore_sub")}</span></span>
        <ArrowRight size={16} className="text-zinc-400 shrink-0" aria-hidden="true" />
      </Link>

      {/* Publicações recentes — Feed (autenticados) / convite (visitantes) */}
      <section aria-labelledby="home-activity-title" className="mb-6" data-testid="home-activity">
        <SectionHeader id="home-activity-title" title={t("community_activity")} to="/feed" t={t} />
        {!user && (
          <Link to="/login" data-testid="home-activity-login" className="flex items-center gap-3 bg-white border border-dashed border-zinc-300 rounded-2xl p-4 text-sm hover:border-[#c8102e]">
            <span className="w-10 h-10 rounded-xl bg-[#c8102e]/10 text-[#c8102e] flex items-center justify-center shrink-0" aria-hidden="true"><LogIn size={18} /></span>
            <span className="flex-1"><span className="block font-semibold">{t("activity_login_title")}</span><span className="block text-xs text-zinc-600">{t("activity_login_sub")}</span></span>
            <ArrowRight size={16} className="text-zinc-400" aria-hidden="true" />
          </Link>
        )}
        {user && activity === null && <div className="h-24 rounded-2xl bg-white border border-zinc-200 animate-pulse" aria-hidden="true" />}
        {user && activity && activity.length === 0 && <EmptyCard label={t("feed_empty")} wide />}
        {user && activity && activity.length > 0 && (
          <div className="space-y-3">
            {activity.map((it) => <FeedCard key={`${it.type}:${it.id}`} item={it} user={user} onRemoved={removeActivity} />)}
          </div>
        )}
        {user && <Link to="/feed" data-testid="home-activity-more" className="mt-3 flex items-center justify-center gap-2 text-sm font-semibold text-[#c8102e] py-3 rounded-2xl bg-white border border-zinc-200 hover:border-[#c8102e]"><Newspaper size={16} aria-hidden="true" /> {t("see_feed")}</Link>}
      </section>

      {/* Eventos em destaque — só quando há conteúdo */}
      {featuredEvents.length > 0 && (
        <section aria-labelledby="home-events-title" className="mb-6" data-testid="home-featured-events">
          <SectionHeader id="home-events-title" title={t("featured_events")} to="/eventos" t={t} />
          <div className="flex overflow-x-auto snap-x snap-mandatory gap-3 pb-2 -mx-4 px-4 scrollbar-none">
            {featuredEvents.map((e) => (
              <Link key={e.id} to={`/eventos/${e.id}`} data-testid={`home-event-${e.id}`} className="snap-start shrink-0 w-56 rounded-2xl overflow-hidden bg-white border border-zinc-200 card-hover">
                <div className="relative aspect-[4/3] bg-zinc-100">
                  {e.image_url && <img src={e.image_url} alt="" loading="lazy" className="w-full h-full object-cover" />}
                  <span className="absolute top-2 right-2 chip !py-1 !px-2 text-[10px]">{e.category}</span>
                </div>
                <div className="p-3">
                  <div className="font-semibold text-sm leading-tight mb-1 line-clamp-2">{e.name}</div>
                  <div className="text-xs text-zinc-600 flex items-center gap-1"><MapPin size={12} aria-hidden="true" /> {e.city} · {new Date(e.date).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB")}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Marketplace */}
      <SectionHeader title={t("discover_marketplace")} to="/marketplace" t={t} />
      <div data-testid="home-discover" className="flex overflow-x-auto snap-x snap-mandatory gap-3 pb-2 -mx-4 px-4 scrollbar-none mb-6">
        {discover && discover.length === 0 && <EmptyCard label={t("empty_offers")} />}
        {(discover || []).map((item) => <DiscoverCard key={`${item.kind}-${item.id}`} item={item} />)}
      </div>

      {/* Angolanas em UK — comunidade oficial reservada */}
      {women && <div className="-mt-2 mb-4" data-testid="home-women-card" data-state={women.my_membership?.status || "none"}>
        <HomeCommunityCard g={women} testid="home-women-card-link"
          icon={<div className="w-11 h-11 rounded-xl shrink-0 overflow-hidden flex items-center justify-center bg-[#F8F0E3]" data-testid="home-women-card-logo"><img src={women.logo_url || "/brand/angolanas-logo.png"} alt="" className="w-full h-full object-contain" /></div>}
          meta={<><Lock size={11} aria-hidden="true" /> Comunidade privada{women.my_membership?.status === "pending" ? " · Pedido pendente" : ""}</>} />
      </div>}

      {/* Comunidades populares */}
      <SectionHeader title={t("popular_communities")} to="/comunidade" t={t} />
      <div className="space-y-2 mb-6">
        {communities.map((g) => (
          <HomeCommunityCard key={g.id} g={g} testid={`home-community-${g.id}`}
            meta={<><MapPin size={11} aria-hidden="true" /> {g.city || (g.scope === "national" ? "Reino Unido" : "")}{showCounts && g.members_count ? ` · ${g.members_count} ${t("members")}` : ""}</>} />
        ))}
      </div>
    </div>
  );
}

// Cartão comum de comunidade (Início): abre a comunidade / apresentação pública; nunca envia pedidos de adesão
function HomeCommunityCard({ g, meta, icon, testid }) {
  return (
    <Link to={`/comunidade/${g.id}`} data-testid={testid} className="bg-white rounded-2xl border border-zinc-200 p-3 flex items-center gap-3 card-hover focus-visible:ring-2 focus-visible:ring-[#c8102e]">
      {icon || <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-red-500 to-black flex items-center justify-center text-white font-bold text-base shrink-0" aria-hidden="true">{g.name.slice(0, 2).toUpperCase()}</div>}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">{g.name}</div>
        <div className="text-xs text-zinc-600 flex items-center gap-1">{meta}</div>
      </div>
      <ArrowRight size={16} className="text-zinc-400 shrink-0" aria-hidden="true" />
    </Link>
  );
}

function SectionHeader({ id, title, to, t }) {
  return (
    <div className="flex items-center justify-between mb-2.5">
      <h2 id={id} className="font-display text-base md:text-lg font-bold">{title}</h2>
      <Link to={to} className="text-xs font-semibold text-[#c8102e] hover:underline min-h-[40px] inline-flex items-center pl-2">{t("see_all")} →</Link>
    </div>
  );
}

function EmptyCard({ label, wide }) {
  return <div className={`${wide ? "w-full" : "w-64"} h-28 rounded-2xl border border-dashed border-zinc-300 flex items-center justify-center text-xs text-zinc-500 text-center px-4`}>{label}</div>;
}
